import type { Monaco } from "@monaco-editor/react";
import Editor from "@monaco-editor/react";
import type { editor } from "monaco-editor";
import * as monaco from "monaco-editor/esm/vs/editor/editor.api";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useMondayTheme } from "@/components/AppContext";
import { useServiceContext } from "@/services";
import { mondayThemes, type Theme } from "@/services/monday";

import { CommandEditorPlaceholder } from "./CommandEditorPlaceholder";
import { getMonacoThemeData, getMonacoThemeName, language } from "./monaco";

interface Props {
  value: string;
  disabled: boolean;
  placeholder?: string;
  autoHeight?: boolean;
  onChange?: (value: string) => void;
}

export function CommandEditor({ value, disabled, placeholder, autoHeight = false, onChange }: Props) {
  const { analytics } = useServiceContext();
  const monacoRef = useRef<editor.IStandaloneCodeEditor>();
  const mondayThemeName = useMondayTheme();
  const [placeholderIsVisible, setPlaceholderIsVisible] = useState(placeholder !== null);
  const height = autoHeight ? "" : "104px";

  const defineOrUpdateTheme = useCallback((mondayTheme: Theme, monaco: Monaco) => {
    const monacoTheme = getMonacoThemeData(mondayTheme);
    monaco.editor.defineTheme(monacoTheme.name, monacoTheme.data);
  }, []);

  const beforeMount = useCallback(
    (monaco: Monaco) => {
      monaco.languages.register({ id: "acli-script" });
      monaco.languages.setMonarchTokensProvider("acli-script", language);

      mondayThemes.forEach((tName) => {
        defineOrUpdateTheme(tName, monaco);
      });
    },
    [defineOrUpdateTheme]
  );

  const setupAutoHeight = useCallback((editor: editor.IStandaloneCodeEditor) => {
    const updateHeight = () => {
      const width = editor.getLayoutInfo().width;
      const contentHeight = Math.min(1000, editor.getContentHeight());
      editor.layout({ width, height: contentHeight });
    };
    editor.onDidContentSizeChange(updateHeight);
    updateHeight();
  }, []);

  const updatePlaceholder = useCallback((editor?: editor.IStandaloneCodeEditor) => {
    setPlaceholderIsVisible(!editor?.hasTextFocus() && editor?.getValue() === "");
  }, []);

  const onMount = useCallback(
    (editor: editor.IStandaloneCodeEditor) => {
      monacoRef.current = editor;
      if (onChange) {
        editor.onDidChangeModelContent(() => {
          onChange(editor.getValue());
        });
      }

      updatePlaceholder(editor);

      editor.onDidBlurEditorWidget(() => {
        updatePlaceholder(editor);
      });

      editor.onDidFocusEditorWidget(() => {
        updatePlaceholder(editor);
      });

      editor.onDidFocusEditorText(() => {
        analytics.sendCommandBoxEdited().catch(console.error);
      });

      if (autoHeight) {
        setupAutoHeight(editor);
      }
    },
    [onChange, autoHeight, setupAutoHeight, analytics, updatePlaceholder]
  );

  const monacoThemeName = useMemo(() => getMonacoThemeName(mondayThemeName), [mondayThemeName]);

  useEffect(() => {
    defineOrUpdateTheme(mondayThemeName, monaco);
    updatePlaceholder(monacoRef.current);
  }, [value, mondayThemeName, defineOrUpdateTheme, updatePlaceholder]);

  return (
    <div style={{ position: "relative" }}>
      {placeholder && <CommandEditorPlaceholder isVisible={placeholderIsVisible} placeholder={placeholder} />}
      <Editor
        className="command-editor"
        beforeMount={beforeMount}
        onMount={onMount}
        defaultLanguage="acli-script"
        value={value}
        height={height}
        theme={monacoThemeName}
        options={{
          automaticLayout: true,
          fontFamily: "monospace",
          fontSize: 14,
          lineNumbers: "off",
          minimap: { enabled: false },
          padding: { top: 10, bottom: 10 },
          occurrencesHighlight: "off",
          quickSuggestions: false,
          readOnly: !!disabled,
          renderLineHighlight: "all",
          scrollbar: {
            alwaysConsumeMouseWheel: !disabled,
          },
          scrollBeyondLastLine: false,
          wordWrap: "on",
          wrappingIndent: "indent",
        }}
      />
    </div>
  );
}
